import { create } from 'zustand';
import { errorMessage } from '../lib/errors.js';
import { FRAIS } from '../api/client.js';
import {
  type Project,
  listProjects,
  createProject,
  updateProject,
  deleteProject,
} from '../api/projects.js';

interface ProjectsState {
  projects: Project[];
  loading: boolean;
  error: string | null;
  load: () => Promise<void>;
  /** Relit au serveur (`FRAIS`) sans passer par `loading` ; une erreur remonte à l'appelant. */
  refresh: () => Promise<void>;
  create: (input: { name: string; color: string; hideFromAgenda: boolean }) => Promise<Project>;
  update: (pid: string, patch: Partial<Project>) => Promise<void>;
  remove: (pid: string) => Promise<void>;
  reorder: (projectIds: string[]) => Promise<void>;
  find: (pid: string) => Project | undefined;
}

export const useProjects = create<ProjectsState>((set, get) => ({
  projects: [],
  loading: false,
  error: null,
  load: async () => {
    set({ loading: true, error: null });
    try {
      const projects = (await listProjects()).sort((a, b) => a.order - b.order);
      set({ projects, loading: false });
    } catch (err) {
      set({ error: errorMessage(err), loading: false });
    }
  },
  refresh: async () => {
    const projects = (await listProjects(FRAIS)).sort((a, b) => a.order - b.order);
    set({ projects, error: null });
  },
  create: async (input) => {
    const project = await createProject({ ...input, order: Date.now() });
    set({ projects: [...get().projects, project].sort((a, b) => a.order - b.order) });
    return project;
  },
  update: async (pid, patch) => {
    const updated = await updateProject(pid, patch);
    set({
      projects: get().projects.map((p) => (p.projectId === pid ? updated : p)),
    });
  },
  remove: async (pid) => {
    await deleteProject(pid);
    set({ projects: get().projects.filter((p) => p.projectId !== pid) });
  },
  reorder: async (projectIds) => {
    const byId = new Map(get().projects.map((p) => [p.projectId, p]));
    const reordered = projectIds
      .map((id, i) => {
        const p = byId.get(id);
        return p ? { ...p, order: i } : null;
      })
      .filter((p): p is Project => p !== null);
    set({ projects: reordered });
    await Promise.all(
      reordered.map((p) => updateProject(p.projectId, { order: p.order }))
    );
  },
  find: (pid) => get().projects.find((p) => p.projectId === pid),
}));
